import { logger } from '@/utils/logger';
import { RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { usePWA } from '@/hooks/usePWA';
import { useState, useEffect } from 'react';
import { useLocation } from '@tanstack/react-router';

export function PWAUpdatePrompt() {
  const { t } = useTranslation();
  const {
    needRefresh,
    offlineReady,
    updateServiceWorker,
    closeUpdatePrompt,
    closeOfflineReady,
  } = usePWA();
  const [isUpdating, setIsUpdating] = useState(false);
  const [shouldShowOfflineReady, setShouldShowOfflineReady] = useState(false);
  const location = useLocation();

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await updateServiceWorker(true);
      // The page will reload automatically after update
    } catch (error) {
      logger.error('Failed to update service worker:', error);
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    if (needRefresh) {
      closeUpdatePrompt();
    } else {
      closeOfflineReady();
      setShouldShowOfflineReady(false);
    }
  };

  // Only show offline ready prompt when user is on dashboard
  useEffect(() => {
    const isDashboard = location.pathname.startsWith('/dashboard');

    if (offlineReady && isDashboard) {
      // Add a delay to avoid showing immediately
      const timer = setTimeout(() => {
        setShouldShowOfflineReady(true);
      }, 2000);

      return () => clearTimeout(timer);
    } else if (!offlineReady) {
      setShouldShowOfflineReady(false);
    }
  }, [offlineReady, location.pathname]);

  // Show update prompt when new version is available
  if (needRefresh) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '4px',
          left: '4px',
          right: '4px',
          zIndex: 99999,
          maxWidth: 'md',
        }}
        className='fixed top-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-md'
      >
        <Card
          style={{
            border: '2px solid #00000020',
            backgroundColor: '#ffffff95',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 0 10px 0 #00000020',
          }}
          className='border-2 border-blue-500/20 bg-background/95 backdrop-blur-sm shadow-lg'
        >
          <div className='flex items-start gap-3 p-4'>
            <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10'>
              <RefreshCw className='h-5 w-5 text-blue-500' />
            </div>

            <div className='flex-1 space-y-1'>
              <h3 className='font-semibold'>{t('pwa.updateAvailable')}</h3>
              <p className='text-sm text-muted-foreground'>
                {t('pwa.updateDescription')}
              </p>

              <div className='flex gap-2 pt-2'>
                <Button
                  onClick={handleUpdate}
                  size='sm'
                  className='flex-1'
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <RefreshCw className='mr-2 h-4 w-4 animate-spin' />
                      {t('pwa.updating')}
                    </>
                  ) : (
                    t('pwa.reloadAndUpdate')
                  )}
                </Button>
                <Button
                  onClick={handleDismiss}
                  size='sm'
                  variant='outline'
                  disabled={isUpdating}
                >
                  {t('pwa.later')}
                </Button>
              </div>
            </div>

            <Button
              onClick={handleDismiss}
              size='icon'
              variant='ghost'
              className='h-8 w-8 shrink-0'
              aria-label={t('pwa.dismissUpdatePrompt')}
              disabled={isUpdating}
            >
              <X className='h-4 w-4' />
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Show offline ready notification (only on dashboard)
  if (shouldShowOfflineReady) {
    return (
      <div className='fixed top-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-md'>
        <Card className='border-2 border-green-500/20 bg-background/95 backdrop-blur-sm shadow-lg'>
          <div className='flex items-start gap-3 p-4'>
            <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-500/10'>
              <RefreshCw className='h-5 w-5 text-green-500' />
            </div>

            <div className='flex-1 space-y-1'>
              <h3 className='font-semibold'>{t('pwa.offlineReady.title')}</h3>
              <p className='text-sm text-muted-foreground'>
                {t('pwa.offlineReady.description')}
              </p>

              <div className='flex gap-2 pt-2'>
                <Button onClick={handleDismiss} size='sm' className='flex-1'>
                  {t('pwa.offlineReady.gotIt')}
                </Button>
              </div>
            </div>

            <Button
              onClick={handleDismiss}
              size='icon'
              variant='ghost'
              className='h-8 w-8 shrink-0'
              aria-label={t('pwa.offlineReady.dismiss')}
            >
              <X className='h-4 w-4' />
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return null;
}
